import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { AlertCircle, Camera, Check, Copy, Download } from 'lucide-react'
import type { Chart } from 'klinecharts'
import { cn } from '@/lib/utils'
import { useWorkbenchSnapshot } from './useWorkbenchSnapshot'
import type { ToolIcon } from './types'

// Chart screenshot. TradingView's camera opens a small menu; we mirror the
// useful subset — save the chart as a PNG, or copy it to the clipboard.
// Captures the WHOLE chart area (canvas + the HTML status line / legends), not
// just the canvas. Sets up the later "send snapshot to AI" idea.

export interface WorkbenchCameraProps {
  chart: Chart | null
  captureRef: RefObject<HTMLDivElement | null>
}

type CopyState = null | 'copied' | 'error'

export function WorkbenchCamera({ chart, captureRef }: WorkbenchCameraProps) {
  const [open, setOpen] = useState(false)
  const [copyState, setCopyState] = useState<CopyState>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const snapshot = useWorkbenchSnapshot(chart, captureRef)

  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false)
        setCopyState(null)
      }
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  const flashCopyState = (s: CopyState) => {
    setCopyState(s)
    if (resetTimer.current) clearTimeout(resetTimer.current)
    resetTimer.current = setTimeout(() => setCopyState(null), 1800)
  }

  const save = async () => {
    await snapshot.save()
    setOpen(false)
    setCopyState(null)
  }

  const copy = async () => {
    flashCopyState((await snapshot.copy()) ? 'copied' : 'error')
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v)
          setCopyState(null)
        }}
        aria-label="Take a snapshot"
        className={cn(
          'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors cursor-pointer',
          'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          open && 'bg-muted text-foreground',
        )}
      >
        <Camera size={15} strokeWidth={1.75} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-44 rounded-md border border-border bg-popover shadow-popover p-1">
          <MenuItem icon={Download} label="Save image" onClick={save} />
          {copyState === 'copied' ? (
            <MenuItem icon={Check} label="Copied" tone="success" />
          ) : copyState === 'error' ? (
            <MenuItem icon={AlertCircle} label="Copy failed" tone="error" />
          ) : (
            <MenuItem icon={Copy} label="Copy image" onClick={copy} />
          )}
        </div>
      )}
    </div>
  )
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  tone,
}: {
  icon: ToolIcon
  label: string
  onClick?: () => void
  tone?: 'success' | 'error'
}) {
  // tone set => a transient confirmation row (not clickable): success neutral-foreground
  // with a check, error in the destructive colour (a system error, not a verdict).
  const isStatus = tone != null
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isStatus}
      className={cn(
        'w-full flex items-center gap-2 text-left text-body px-2 py-1.5 rounded-md transition-colors',
        isStatus
          ? tone === 'error'
            ? 'text-destructive cursor-default'
            : 'text-foreground cursor-default'
          : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer',
      )}
    >
      <Icon size={14} strokeWidth={1.75} />
      {label}
    </button>
  )
}
